import { DateTime } from 'luxon';

import { formatPcDate } from 'app/shared/utils/format-date';
import { fromServerDate } from 'app/shared/utils/luxon-date';

/** A relative moment ("hace 2 h"), ready for the `| translate` pipe. */
export interface RelativeTimeLabel {
  labelKey: string;
  params?: Record<string, string | number>;
  /** The full corrected timestamp, for the `title` attribute. */
  exact: string;
}

/** Which calendar day a moment belongs to, for day-grouped feeds. */
export type DayBucket = 'TODAY' | 'YESTERDAY' | 'EARLIER';

/**
 * A moment expressed relative to now, for feeds where "when" matters more than
 * "exactly when" — a notification is two hours old, not stamped 03/08/2026 17:19.
 *
 * The value goes through `fromServerDate`: API timestamps serialize without an
 * offset, so reading one as local time shifts it by the viewer's offset (five
 * hours in Guayaquil). The exact stamp travels alongside for the tooltip, so the
 * precise moment is always one hover away.
 *
 * Buckets: under a minute, then minutes, then hours for the first day, then
 * yesterday by name, then days up to a week, then a plain date. Hours are
 * measured by elapsed time rather than by calendar day so a 20-hour-old item
 * reads "hace 20 h" instead of jumping to "ayer".
 *
 * @param value An API timestamp (ISO string), Date, or millis.
 * @param now Reference moment; defaults to the current time. Pass it to keep a
 *   list of labels consistent, and to make tests deterministic.
 * @returns The label, or null when the value can't be parsed — callers should
 *   render nothing rather than "Invalid DateTime".
 */
export function relativeTimeLabel(value: unknown, now?: DateTime): RelativeTimeLabel | null {
  const moment = fromServerDate(value);
  if (!moment.isValid) {
    return null;
  }

  const reference = now ?? DateTime.now();
  const exact = formatPcDate(moment, 'datetime') ?? '';
  const elapsedMinutes = reference.diff(moment, 'minutes').minutes;

  // A stamp in the future (clock skew between the API host and the viewer)
  // reads as just-now rather than as a negative age.
  if (elapsedMinutes < 1) {
    return { labelKey: 'RELATIVE_NOW', exact };
  }
  if (elapsedMinutes < 60) {
    return { labelKey: 'RELATIVE_MINUTES', params: { min: Math.floor(elapsedMinutes) }, exact };
  }
  if (elapsedMinutes < 60 * 24) {
    return {
      labelKey: 'RELATIVE_HOURS',
      params: { hours: Math.floor(elapsedMinutes / 60) },
      exact,
    };
  }

  const days = Math.floor(reference.startOf('day').diff(moment.startOf('day'), 'days').days);
  if (days === 1) {
    return { labelKey: 'RELATIVE_YESTERDAY', exact };
  }
  if (days < 7) {
    return { labelKey: 'RELATIVE_DAYS', params: { days }, exact };
  }
  return {
    labelKey: 'RELATIVE_DATE',
    params: { date: formatPcDate(moment, 'date') ?? '' },
    exact,
  };
}

/**
 * The calendar day a moment falls on, relative to now — the section a row
 * belongs to in a day-grouped feed.
 *
 * Calendar-based, unlike the hour buckets in `relativeTimeLabel`: an item from
 * 23:50 last night belongs under "Ayer" even though it is only minutes old.
 * Reads the value through `fromServerDate` for the same reason.
 *
 * Unparseable values fall into `EARLIER`, which sorts last and states nothing
 * false about the moment.
 */
export function dayBucket(value: unknown, now?: DateTime): DayBucket {
  const moment = fromServerDate(value);
  if (!moment.isValid) {
    return 'EARLIER';
  }
  const reference = now ?? DateTime.now();
  if (moment.hasSame(reference, 'day')) {
    return 'TODAY';
  }
  if (moment.hasSame(reference.minus({ days: 1 }), 'day')) {
    return 'YESTERDAY';
  }
  return 'EARLIER';
}
